import React, {useEffect, useRef, useState} from 'react';
import Utils from "../../../../utils/Utils";
import MenuCategoryProduct from "./MenuCategoryProduct";
import {connect} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {RootState} from "../../../../../reducers";
import {getAvailabilities, getMenus} from "../../../../services/Menu/reducer";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import MenuCategoryAvailabilityInfo from "./MenuCategoryAvailabilityInfo";
import ResponsiveComponent from "../../../../components/Common/ResponsiveComponent";
import MenuCategoryAvailabilityHeading from "./MenuCategoryAvailabilityHeading";
import {MenuProductViewType, OrderType} from "../../../../services/Api/types";
import ScrollableTabs from "../../../../components/Common/ScrollableTabs";
import {ReactComponent as ArrowIco} from "../../../../../images/icons/arrow-right.svg";
import {Link} from "react-router-dom";
import AppConfig from "../../../../services/AppConfig";
import {usePrefix} from "../../../../services/Prefix";
import CategoryHelper from "../../../../services/Menu/services/Category/CategoryHelper";
import {getParsedSessionOrderType} from "../../../../utils/order";
import {getStoreConfig} from "../../../../services/Store/reducer";
import StoreUtils from "../../../../services/Store/StoreUtils";

interface OwnProps {
    category: any;
    promotions: any[];
    addItem: (id: number) => void;
    forceExpandItems?: true;
    setAvailableCategoryIds: React.Dispatch<React.SetStateAction<number[]>>;
    productsViewType: MenuProductViewType;
    menuId: number | undefined;
}

const areItemsExpanded = (isCategoryAvailable: boolean, anyCategoryItemAvailable: boolean, force?: boolean) => {
    if (force) return true;

    if (isCategoryAvailable) {
        return anyCategoryItemAvailable;
    }
    return false;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const MenuCategorySection = ({
                                 category,
                                 promotions,
                                 addItem,
                                 menus,
                                 session,
                                 availabilities,
                                 locale,
                                 forceExpandItems,
                                 setAvailableCategoryIds,
                                 productsViewType,
                                 config,
                                 menuId
                             }: Props) => {
    const isReadonly = StoreUtils.isOrderingReadonly(config);
    const items = CategoryHelper.getSortedCategoryItems(category);
    const orderType = session.order_type as OrderType;
    const parsedOrderType = getParsedSessionOrderType(orderType);
    const menusWithCategory = CategoryHelper.getMenusForCategoryByIds(category, menus);
    const menusWithCategoryForCurrentOrderType = menusWithCategory.filter((menu: any) => menu[parsedOrderType.toLowerCase()] === true);
    const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: any) => menu.availability_id));
    const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(session.pickup_at));
    const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));
    const isCategoryAvailable = isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;
    const availableCategoryItems = CategoryHelper.getAvailableCategoryItems(availabilities, session.pickup_at, items);
    const isFinallyAvailable = isReadonly ? true : isCategoryAvailable ? isCategoryAvailable && !!availableCategoryItems.length : isCategoryAvailable;
    const [itemsExpanded, setItemsExpanded] = useState(areItemsExpanded(isCategoryAvailable, !!availableCategoryItems.length, forceExpandItems));
    const [itemsOverflow, setItemsOverflow] = useState(false);
    const scrollableWrapperRef = useRef<HTMLDivElement | null>(null);
    const prefix = usePrefix();
    const {t} = useTranslation();
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [maxVisibleItems, setMaxVisibleItems] = useState(0);

    useEffect(() => {
        setAvailableCategoryIds((prevState) => {
            const existingId = prevState.find(prevStateValue => prevStateValue === category.id);

            if (isFinallyAvailable) {
                if (existingId) return prevState;

                return [...prevState, category.id];
            }

            if (existingId) {
                const index = prevState.indexOf(existingId);
                const newPrevState = [...prevState];
                newPrevState.splice(index, 1);
                return newPrevState;
            }

            return prevState;
        })
    }, [isFinallyAvailable]);

    useEffect(() => {
        if (productsViewType !== MenuProductViewType.HORIZONTAL) {
            setItemsOverflow(false);
            return;
        }

        const wrapper = scrollableWrapperRef.current;
        if (!wrapper) return;

        const scrollableElement = wrapper.querySelector('.menu-items-horizontal-list > .indiana-scroll-container') as HTMLElement | null;

        if (!scrollableElement) return;

        const checkOverflow = () => {
            const hasOverflow = scrollableElement.scrollWidth > scrollableElement.clientWidth + 5;
            setItemsOverflow(prev => {
                if (prev !== hasOverflow) return hasOverflow;
                return prev;
            });
        };

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(checkOverflow);
        });
        resizeObserver.observe(scrollableElement);

        const mutationObserver = new MutationObserver(checkOverflow);
        mutationObserver.observe(scrollableElement, {childList: true, subtree: true, attributes: true});

        checkOverflow();
        setTimeout(checkOverflow, 50);

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [items, productsViewType]);

    useEffect(() => {
        if (!gridRef.current || productsViewType !== MenuProductViewType.VERTICAL_BIG_IMAGE) return;

        const container = gridRef.current;
        const CARD_MIN_WIDTH = 187;
        const GAP = 8;
        const ROWS = 1;

        const calculate = () => {
            const containerWidth = container.clientWidth;
            const itemsPerRow = Math.max(
                1,
                Math.floor((containerWidth + GAP) / (CARD_MIN_WIDTH + GAP))
            );

            setMaxVisibleItems(itemsPerRow * ROWS);
        };

        const observer = new ResizeObserver(() => {
            requestAnimationFrame(calculate);
        });

        observer.observe(container);
        calculate();

        return () => observer.disconnect();
    }, [productsViewType]);

    const renderItems = (smallScreen?: boolean) => {
        return getItems(smallScreen).map((item: any) => {
                return <MenuCategoryProduct promotions={promotions}
                                            addItem={addItem}
                                            category={category}
                                            viewType={productsViewType}
                                            forceNotAvailable={!isFinallyAvailable}
                                            itemsExpanded={itemsExpanded}
                                            product={item}
                                            key={`item-${category.id}-${item.type}-${item.id}`}/>
            }
        )
    };

    const getItems = (smallScreen?: boolean) => {
        if (productsViewType === MenuProductViewType.HORIZONTAL) return items;

        if (isFinallyAvailable || itemsExpanded) return items;

        if (productsViewType === MenuProductViewType.VERTICAL_BIG_IMAGE) {
            return smallScreen ? items.filter((_, index) => index === 0) : items.filter((_, index) => index < maxVisibleItems);
        }

        return smallScreen ? items.filter((_, index) => index === 0) : items.filter((_, index) => index < 2);
    }

    const getCategoryPageLink = () => {
        if (StoreUtils.isMultilevelMenu(config)) {
            return AppConfig.getLinkWithPrefix(`/menu/${menuId}/category/${category.id}`, prefix);
        }

        return AppConfig.getLinkWithPrefix(`/menu/category/${category.id}`, prefix);
    }

    const drawAvailabilityHeading = () => {
        if (productsViewType === MenuProductViewType.HORIZONTAL) {
            if (!isFinallyAvailable) return <MenuCategoryAvailabilityHeading pickupDate={session.pickup_at}
                                                                             category={category} orderType={orderType}
                                                                             items={items}
                                                                             locale={locale}
                                                                             config={config}
                                                                             availabilities={availabilities}
                                                                             menus={menusWithCategory}/>;
        }

        return !isFinallyAvailable && itemsExpanded &&
            <MenuCategoryAvailabilityHeading pickupDate={session.pickup_at} locale={locale}
                                             orderType={orderType} category={category} menus={menusWithCategory}
                                             availabilities={availabilities} config={config}
                                             items={items}/>
    }

    return (
        <section className="menu-category container-fluid scrollspy-section"
                 id={`category-${session.order_type}-${category.id}`}>
            <div className="menu-category-header">
                <div className="d-flex align-items-start justify-content-between gap-2 ">
                    <h1 className="h4">{category.name}</h1>
                    {itemsOverflow && (
                        <Link to={getCategoryPageLink()}
                              className="text-muted text-decoration-none d-flex align-items-center">
                            <span className="me-2 fw-bolder">{t("common.word.all")}</span>
                            <ArrowIco/>
                        </Link>
                    )}
                </div>
                {category.description && (
                    <p dangerouslySetInnerHTML={{__html: Utils.nl2brString(category.description)}}></p>
                )}
                {drawAvailabilityHeading()}
            </div>
            {
                productsViewType === MenuProductViewType.VERTICAL_SMALL_IMAGE && (
                    <div
                        className={`menu-items menu-items-grid ${!isFinallyAvailable && itemsExpanded ? 'menu-items-not-available' : ''}`}>
                        {!isFinallyAvailable && !itemsExpanded &&
                            <MenuCategoryAvailabilityInfo
                                pickupDate={session.pickup_at} locale={locale}
                                orderType={orderType} category={category} menus={menusWithCategory}
                                availabilities={availabilities} config={config}
                                items={items} onClick={() => setItemsExpanded(true)}
                            />}
                        <ResponsiveComponent small={renderItems(true)} large={renderItems()} breakpoint={992}/>
                    </div>
                )
            }
            {
                productsViewType === MenuProductViewType.HORIZONTAL && (
                    <div ref={scrollableWrapperRef}>
                        <ScrollableTabs hideScrollbars={false} scrollStep={250}
                                        className={`menu-items menu-items-horizontal-list ${!isFinallyAvailable ? 'menu-items-not-available' : ''}`}>
                            {renderItems()}
                        </ScrollableTabs>
                    </div>
                )
            }
            {
                productsViewType === MenuProductViewType.VERTICAL_BIG_IMAGE && (
                    <div ref={gridRef}
                         className={`menu-items menu-items-grid-with-big-images ${!isFinallyAvailable && itemsExpanded ? 'menu-items-not-available' : ''}`}>
                        {!isFinallyAvailable && !itemsExpanded &&
                            <MenuCategoryAvailabilityInfo
                                pickupDate={session.pickup_at} locale={locale}
                                orderType={orderType} category={category} menus={menusWithCategory}
                                availabilities={availabilities} config={config}
                                items={items} onClick={() => setItemsExpanded(true)}
                            />}
                        <ResponsiveComponent small={renderItems(true)} large={renderItems()} breakpoint={430}/>
                    </div>
                )
            }
        </section>
    )
}

const mapStateToProps = (state: RootState) => ({
    menus: getMenus(state.menu),
    session: getSession(state.session),
    availabilities: getAvailabilities(state.menu),
    locale: selectLocale(state.session),
    config: getStoreConfig(state.config),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(MenuCategorySection));